import {Component, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {UntypedFormControl} from "@angular/forms";
import {merge, of} from "rxjs";
import {MatPaginator} from "@angular/material/paginator";
import {catchError, map, startWith, switchMap} from "rxjs/operators";
import {HttpParams} from "@angular/common/http";
import {CategoryService} from "../../services/vocab/category.service";
import {NotificationService} from "../../services/common/notification.service";
import {SelectionModel} from "@angular/cdk/collections";
import {Type} from "../../shared/Type";
import {WordService} from "../../services/vocab/word.service";
import {Word} from "../../dtos/vocab/entry/word";
import {SysGrammarService} from "../../services/grammar/sys-grammar.service";

// TODO check with Max
export interface DialogData {
  selectedObjects: any[];
  dialogTitle: string;
  selectionEntity: any;
  displayedColumns: string[];
  selectedObject: any;
  excludedId: number;
  selectionMode: string;

}

@Component({
  selector: 'app-single-select',
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss'
})
export class SelectComponent {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  selectedObject: any;
  selectedObjects: any[];

  displayedColumns: string[];
  searchControl = new UntypedFormControl('');

  selection = new SelectionModel<any>(false, []);
  matchingData: any[];

  isLoadingResults = true;
  isRateLimitReached = false;
  resultsLength = 25;

  selectionEntity: any;
  dialogTitle: string;
  excludedId: number;

  selectionMode: string;

  constructor(@Inject(MAT_DIALOG_DATA) public injectedData: DialogData,
              private categoryService: CategoryService,
              private wordService: WordService,
              private sysGrammarService: SysGrammarService,
              private notificationService: NotificationService,
              private dialogRef: MatDialogRef<SelectComponent>) {
    this.selectionMode = injectedData.selectionMode;
    this.excludedId = injectedData.excludedId;

    if (this.selectionMode === 'single-select') {
      this.selectedObject = injectedData.selectedObject;
      if (Array.isArray(this.selectedObject)) {
        this.selectedObject = this.selectedObject[0];
      }
    }

    if (this.selectionMode === 'multi-select') {
      this.selectedObjects = injectedData.selectedObjects;
      if (this.selectedObjects == null) {
        this.selectedObjects = [];
      }
    }

    this.displayedColumns = injectedData.displayedColumns;
    this.selectionEntity = injectedData.selectionEntity;
    this.dialogTitle = injectedData.dialogTitle;

    dialogRef.afterOpened().subscribe(() => this.search());
  }

  search() {
    merge(this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          let params = new HttpParams();
          params = params.append('name', String((this.searchControl.value == null) ? '' : this.searchControl.value));
          params = params.append('size', String(this.paginator.pageSize));
          params = params.append('page', String(this.paginator.pageIndex));
          if (this.excludedId) {
            params = params.append('exclude', this.excludedId);
          }
          if (this.selectionEntity === 'vocab_category') {
            return this.categoryService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'vocab_entry') {
            return this.wordService.searchEntries(params)
              .pipe(catchError(() => of(null)));
          }
          if (this.selectionEntity === 'systemic_grammar_category') {
            return this.sysGrammarService.searchCategories(params)
              .pipe(catchError(() => of(null)));
          }
        }),
        map(data => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.isRateLimitReached = data === null;

          if (data === null) {
            return [];
          }
          // Only refresh the result length if there is new data. In case of rate
          // limit errors, we do not want to reset the paginator to zero, as that
          // would prevent users from re-triggering requests.
          this.resultsLength = data.totalElements;
          return data.content;
        })
      ).subscribe(data => {
      this.matchingData = data;
      this.selection.clear();

      if (this.selectionMode === 'single-select') {
        if (this.selectedObject !== undefined) {
          const i = this.getObjectWithId(this.selectedObject?.id);
          if (i !== null) {
            this.selection.select(this.getObjectWithId(this.selectedObject?.id));
          }
        }
      }

      if (this.selectionMode === 'multi-select') {
        this.selectedObjects.forEach(o => {
          const i = this.getObjectWithId(o.id);
          if (i !== null) {
            this.selection.select(this.getObjectWithId(o.id));
          }
        })
      }
    }, error => {
      this.notificationService.openSnackBar(error);
    });
  }

  getObjectWithId(id: number) {
    let v = null;
    this.matchingData.forEach(c => {
      if (c.id === id) {
        v = c;
      }
    });
    return v;
  }

  changeSelection(row?: any) {
    this.selection.toggle(row);
    if (!this.selection.isSelected(row)) {
      this.selectedObject = null;
    } else {
      this.selectedObject = row;
    }
  }

  typeEnumToString(type: Type): string {
    if (type == null) {
      return '';
    } else {
      return Type[type];
    }
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.selected.forEach(s => {
        this.selectedObjects = this.selectedObjects.filter(w => w.id !== s.id);
      });
      this.selection.clear();
      return;
    }

    this.matchingData.forEach(w => {
      this.selection.select(w);
      this.selectedObjects.push(w);
    });
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.paginator?.pageSize;
    return (numSelected === numRows) || (numSelected > numRows);
  }


  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Word): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id}`;
  }


  closeDialog() {
    if (this.selectionMode === 'single-select') {
      this.dialogRef.close([this.selectedObject]);
    }

    if (this.selectionMode === 'multi-select') {
      this.dialogRef.close(this.selectedObjects);
    }
  }

  returnInitialData() {
    if (this.selectionMode === 'single-select') {
      this.dialogRef.close([this.injectedData.selectedObject]);
    }

    if (this.selectionMode === 'multi-select') {
      this.dialogRef.close(this.injectedData.selectedObjects);
    }
  }

  deselectObject() {
    this.selection.clear();
    this.selectedObject = null;
  }

}
